import type React from 'react';
import { useTranslation } from 'react-i18next';
import PackageTable from '@/features/Overview/components/PackageTable';
import {
	buildCoverageSummary,
	buildProductEntries
} from '@/features/Overview/utils/productEntries';
import type { AppData, ApplicationData } from '@/types/app';
import type { UseRequestQuotesResult } from '../hooks/useRequestQuotes';
import { getProductLabel } from '../utils/productLabels';
import SectionCard from './SectionCard';

interface PackageOverviewProps {
	quotes: UseRequestQuotesResult;
	appData: AppData;
	applicationData: ApplicationData;
}

/**
 * "Overzicht pakket" — the same priced package table the customer sees on the
 * overview page, reusing its entry builder and markup. Product labels go through
 * the specialist naming used elsewhere on this page.
 */
const PackageOverview = ({
	quotes,
	appData,
	applicationData
}: PackageOverviewProps): React.JSX.Element | null => {
	const { t } = useTranslation();

	const entries = buildProductEntries({
		relevantProductTypes: appData.relevant_product_types ?? [],
		applicationData,
		appData,
		...quotes.quoteLists,
		t
	}).map((entry) => ({ ...entry, label: getProductLabel(t, entry.key) }));

	const selectedEntries = entries.filter((entry) => entry.selectedQuote !== null);
	if (selectedEntries.length === 0) return null;

	const totalPremiumEur = selectedEntries.reduce((sum, entry) => sum + (entry.premiumEur ?? 0), 0);

	const coverageSummary = buildCoverageSummary(
		appData.premium_variables?.coverage?.sick_leave_short_term,
		t
	);

	return (
		<SectionCard
			title={t('requestOverview.packageTitle')}
			testId="dossier-section-package"
			bodyClassName="p-0 sm:p-0 bg-transparent"
		>
			<PackageTable
				entries={selectedEntries}
				totalPremiumEur={totalPremiumEur}
				coverageSummary={coverageSummary}
			/>
		</SectionCard>
	);
};

export default PackageOverview;
